import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { ITEMS_PER_PAGE } from '../utils/constants';

export const useStudents = (userType = null) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessToken, userId } = useAuth();
  
  // 데이터 상태
  const [studentsData, setStudentsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Teacher 상태 추가 (통합 관리)
  const [teachers, setTeachers] = useState([]);
  const [isTeachersLoading, setIsTeachersLoading] = useState(false);
  
  // 중복 호출 방지를 위한 ref - location별로 추적
  const initialLoadExecutedRef = useRef({});
  const teachersFetchedRef = useRef(false);
  
  // 단일 학생 조회 상태 추가
  const [isLoadingSingleStudent, setIsLoadingSingleStudent] = useState(false);
  const [singleStudentError, setSingleStudentError] = useState(null);
  
  // 학생 캐시 시스템 추가
  const studentCacheRef = useRef(new Map()); // student_id -> student 객체
  const fetchingStudentsRef = useRef(new Set()); // 현재 조회 중인 student_id들
  
  // 필터 관련 상태
  const searchParams = new URLSearchParams(location.search);
  const [filters, setFilters] = useState({
    student_name: searchParams.get('student_name') || '',
    in_charge_teacher: searchParams.get('in_charge_teacher') || '',
    lecture: searchParams.get('lecture') || '',
    school_name: searchParams.get('school_name') || ''
  });
  const [isFilterMode, setIsFilterMode] = useState(
    searchParams.get('student_name') || 
    searchParams.get('in_charge_teacher') || 
    searchParams.get('lecture') ||
    searchParams.get('school_name')
  );
  
  // 인피니티 스크롤 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  
  // 모달 상태
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStudentDetail, setSelectedStudentDetail] = useState(null);
  
  // 네비게이션 상태 추적 - 학생 목록 복원용
  const [navigationState, setNavigationState] = useState('list'); // 'list' | 'student' | 'test'
  const [shouldResetToFullList, setShouldResetToFullList] = useState(false);

  // Teacher 데이터 가져오기 함수
  const fetchTeachers = useCallback(async () => {
    // teacher 권한인 경우 불필요
    if (userType === 'teacher' || teachersFetchedRef.current) {
      return;
    }

    try {
      setIsTeachersLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/teacher?page=1&per_page=50`, {
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        // 담당업무가 "원장", "강사", "팀장"인 경우만 필터링
        const filteredTeachers = data.data?.filter(teacher => 
          ['원장', '강사', '팀장'].includes(teacher.charge_work)
        ) || [];
        setTeachers(filteredTeachers);
        teachersFetchedRef.current = true;
      }
    } catch (error) {
      console.error('강사 목록 불러오기 실패:', error);
    } finally {
      setIsTeachersLoading(false);
    }
  }, [userType, getAccessToken, navigate]);
  


  // 최적화된 단일 학생 조회 (캐시 + 중복 방지 + 새 API 사용)
  const fetchStudentById = useCallback(async (studentId) => {
    const numericStudentId = parseInt(studentId);
    
    // 캐시에서 먼저 확인
    if (studentCacheRef.current.has(numericStudentId)) {
      console.log(`학생 ID ${studentId} 캐시에서 반환`);
      return studentCacheRef.current.get(numericStudentId);
    }
    
    // 이미 조회 중이면 대기
    if (fetchingStudentsRef.current.has(numericStudentId)) {
      console.log(`학생 ID ${studentId} 이미 조회 중, 대기`);
      return new Promise(resolve => {
        const checkInterval = setInterval(() => {
          if (!fetchingStudentsRef.current.has(numericStudentId)) {
            clearInterval(checkInterval);
            resolve(studentCacheRef.current.get(numericStudentId) || null);
          }
        }, 100);
        
        // 5초 타임아웃
        setTimeout(() => {
          clearInterval(checkInterval);
          resolve(null);
        }, 5000);
      });
    }

    fetchingStudentsRef.current.add(numericStudentId);
    
    try {
      setIsLoadingSingleStudent(true);
      setSingleStudentError(null);
      
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }

      console.log(`학생 ID ${studentId} API 조회 시작...`);

      // 새로운 단일 학생 조회 API 사용
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/student/card?student_id=${numericStudentId}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 404) {
        const errorMsg = `학생 ID ${studentId}를 찾을 수 없습니다.`;
        console.warn(errorMsg);
        setSingleStudentError(errorMsg);
        return null;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        const studentData = result.data;
        
        // 필드명 매핑 (API 응답과 기존 코드의 필드명이 다를 수 있음)
        const normalizedStudent = {
          student_id: studentData.student_id,
          student_name: studentData.student_name,
          student_email: studentData.student_email,
          school_grade: studentData.student_grade || studentData.school_grade,
          school_name: studentData.school_name,
          mobile_number: studentData.mobile_number,
          parent_mobile_number: studentData.parent_mobile_number,
          teacher_name: studentData.in_charge_teacher,
          in_charge_teacher: studentData.in_charge_teacher,
          lecture: studentData.lecture,
          acq_channel: studentData.acq_channel,
          referral_id: studentData.referral_id,
          is_blocked: studentData.is_blocked,
          created_at: studentData.created_at,
          updated_at: studentData.updated_at
        };
        
        // 캐시에 저장
        studentCacheRef.current.set(normalizedStudent.student_id, normalizedStudent);
        
        console.log(`✅ 학생 ID ${studentId} 조회 성공`);
        return normalizedStudent;
      }
      
      throw new Error(`학생 정보 형식이 올바르지 않습니다.`);
    } catch (error) {
      const errorMsg = `학생 ID ${studentId} 조회 실패: ${error.message}`;
      console.error(errorMsg, error);
      setSingleStudentError(errorMsg);
      return null;
    } finally {
      fetchingStudentsRef.current.delete(numericStudentId);
      setIsLoadingSingleStudent(false);
    }
  }, []); // 의존성 제거 - 모든 값을 ref나 직접 호출로 처리

  // 최적화된 URL 변경 감지 및 단일 학생 로드 
  const previousStudentId = useRef(null);
  
  useEffect(() => {
    const pathMatch = location.pathname.match(/\/learning-status\/student\/(\d+)/);
    if (pathMatch) {
      const urlStudentId = parseInt(pathMatch[1]);
      
      // 이전과 같은 학생 ID면 건너뛰기
      if (previousStudentId.current === urlStudentId) {
        return;
      }
      
      previousStudentId.current = urlStudentId;
      
      // 캐시에서 먼저 확인
      if (studentCacheRef.current.has(urlStudentId)) {
        const cachedStudent = studentCacheRef.current.get(urlStudentId);
        setStudentsData(prev => {
          const exists = prev.some(s => s.student_id === cachedStudent.student_id);
          if (exists) return prev;
          return [cachedStudent, ...prev];
        });
        return;
      }
      
      // studentsData에서 확인 
      const existingStudent = studentsData.find(s => s.student_id === urlStudentId);
      if (existingStudent) {
        // 캐시에도 저장
        studentCacheRef.current.set(urlStudentId, existingStudent);
        return;
      }
      
      // 조회 중이 아니고 로딩 중이 아닐 때만 API 호출
      if (!fetchingStudentsRef.current.has(urlStudentId) && !isLoading && !isLoadingSingleStudent) {
        console.log(`URL에서 학생 ID ${urlStudentId} 감지, 단일 조회 시작`);
        fetchStudentById(urlStudentId).then(student => {
          if (student) {
            setStudentsData(prev => {
              const exists = prev.some(s => s.student_id === student.student_id);
              if (exists) return prev;
              return [student, ...prev];
            });
            setSingleStudentError(null);
          }
        });
      }
    } else {
      // URL에 studentId가 없으면 이전 ID 초기화
      previousStudentId.current = null;
      setSingleStudentError(null);
    }
  }, [location.pathname]); // 의존성 최소화 - pathname만 추적
  
  // 네비게이션 상태 추적 - 학생 목록으로 돌아갈 때 전체 목록 복원
  useEffect(() => {
    const isDetailPage = location.pathname.includes('/student/');
    const isListPage = location.pathname === '/learning-status' || 
                       (location.pathname === '/learning-status/' && !location.pathname.includes('/student/'));
    
    let newState = 'list';
    if (location.pathname.includes('/test/')) {
      newState = 'test';
    } else if (isDetailPage) {
      newState = 'student';
    }
    
    console.log('네비게이션 상태 변경:', {
      이전상태: navigationState,
      새상태: newState,
      현재경로: location.pathname,
      isListPage
    });
    
    // 학생 상세에서 목록으로 돌아올 때 전체 목록 복원 트리거
    if (navigationState !== 'list' && newState === 'list') {
      console.log('학생 목록으로 돌아감 - 전체 목록 복원 트리거');
      setShouldResetToFullList(true);
    }
    
    setNavigationState(newState);
  }, [location.pathname, navigationState]);
  
  // 전체 목록 복원 로직
  useEffect(() => {
    if (shouldResetToFullList) {
      console.log('전체 학생 목록 복원 시작');
      
      // 필터와 상태 초기화
      setIsFilterMode(false);
      setFilters({
        student_name: '',
        in_charge_teacher: '',
        lecture: '',
        school_name: ''
      });
      setCurrentPage(1);
      setStudentsData([]);
      setShouldResetToFullList(false);
      
      // URL 쿼리 파라미터도 정리
      if (location.search) {
        navigate('/learning-status', { replace: true });
        return; // navigate 후 새로운 effect가 실행되므로 여기서 종료
      }
      
      // 전체 목록 다시 로드
      (async () => {
        try {
          setIsLoading(true);
          const token = await getAccessToken();
          if (!token) {
            navigate('/signin');
            return;
          }

          const params = new URLSearchParams({
            page: 1,
            per_page: ITEMS_PER_PAGE
          });

          let url;
          if (userType === 'manager') {
            url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
          } else if (userType === 'teacher') {
            params.append('teacher_id', userId);
            url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student/allocate?${params}`;
          } else {
            url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
          }

          const response = await fetch(url, {
            headers: {
              'Authorization': token,
              'Content-Type': 'application/json',
            }
          });

          if (response.ok) {
            const data = await response.json();
            setStudentsData(data.data || []);
            setCurrentPage(data.page || 1);
            setTotalPages(data.total_pages || 1);
            setHasMore((data.page || 1) < (data.total_pages || 1));
            setTotalItems(data.total_items || 0);
            console.log('전체 학생 목록 복원 완료:', data.data?.length || 0, '명');
          }
        } catch (error) {
          console.error('전체 학생 목록 복원 중 오류:', error);
          setError(error.message);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [shouldResetToFullList, userType, userId, getAccessToken, navigate, location.search]);
  
  // 초기 로드 - 중복 호출 방지 적용
  useEffect(() => {
    const currentLocationKey = `${location.pathname}${location.search}`;
    
    // 현재 location에 대해 이미 실행했으면 중복 호출 방지
    if (initialLoadExecutedRef.current[currentLocationKey]) {
      return;
    }

    const hasFilters = location.search.length > 0;
    setIsFilterMode(hasFilters);
    
    const initialFilters = {
      student_name: searchParams.get('student_name') || '',
      in_charge_teacher: searchParams.get('in_charge_teacher') || '',
      lecture: searchParams.get('lecture') || '',
      school_name: searchParams.get('school_name') || ''
    };
    setFilters(initialFilters);
    
    setCurrentPage(1);
    setStudentsData([]);
    
    // URL에 특정 학생 ID가 있으면 초기 목록 로드를 건너뛰고 단일 학생 검색만 수행
    const pathMatch = location.pathname.match(/\/learning-status\/student\/(\d+)/);
    if (pathMatch) {
      console.log('URL에 특정 학생 ID가 있어서 초기 목록 로드 건너뛰기');
      setIsLoading(false); // 로딩 상태만 해제
      
      // 인피니티 스크롤 상태 초기화 (무한 API 호출 방지)
      setHasMore(false);
      setCurrentPage(1);
      setTotalPages(1);
      setIsLoadingMore(false);
      
      initialLoadExecutedRef.current[currentLocationKey] = true; // 실행 완료 표시
      return;
    }
    
    // 일반적인 학생 목록 로드 + Teacher 데이터 로드 (병렬 처리)
    (async () => {
      try {
        setIsLoading(true);
        const token = await getAccessToken();
        if (!token) {
          navigate('/signin');
          return;
        }

        // 학생 목록과 Teacher 목록을 병렬로 호출
        const promises = [];

        // 1. 학생 목록 API 호출
        let url;
        const params = new URLSearchParams({
          page: 1,
          per_page: ITEMS_PER_PAGE
        });

        // 필터 적용
        if (hasFilters) {
          if (initialFilters.student_name) params.append('student_name', initialFilters.student_name);
          if (initialFilters.in_charge_teacher) params.append('in_charge_teacher', initialFilters.in_charge_teacher);
          if (initialFilters.lecture) params.append('lecture', initialFilters.lecture);
          if (initialFilters.school_name) params.append('school_name', initialFilters.school_name);
        }

        // 권한에 따라 다른 엔드포인트 사용
        if (userType === 'manager') {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
        } else if (userType === 'teacher') {
          params.append('teacher_id', userId);
          url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student/allocate?${params}`;
        } else {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
        }

        const studentPromise = fetch(url, {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        });
        promises.push(studentPromise);

        // 2. Teacher 목록 API 호출 (manager 권한에서만)
        let teacherPromise = null;
        if (userType === 'manager' && !teachersFetchedRef.current) {
          teacherPromise = fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/teacher?page=1&per_page=50`, {
            headers: {
              'Authorization': token,
              'Content-Type': 'application/json',
            }
          });
          promises.push(teacherPromise);
        }

        // 병렬 실행
        const responses = await Promise.all(promises);
        
        // 학생 목록 결과 처리
        const studentResponse = responses[0];
        if (studentResponse.ok) {
          const result = await studentResponse.json();
          const students = result.data || [];
          
          // 학생 데이터를 캐시에 저장
          students.forEach(student => {
            studentCacheRef.current.set(student.student_id, student);
          });
          
          setStudentsData(students);
          setTotalPages(result.total_pages || 1);
          setHasMore(1 < (result.total_pages || 1));
          setCurrentPage(1);
          setTotalItems(result.total_items || 0);
        }

        // Teacher 목록 결과 처리
        if (teacherPromise && responses[1]) {
          const teacherResponse = responses[1];
          if (teacherResponse.ok) {
            const teacherData = await teacherResponse.json();
            const filteredTeachers = teacherData.data?.filter(teacher => 
              ['원장', '강사', '팀장'].includes(teacher.charge_work)
            ) || [];
            setTeachers(filteredTeachers);
            teachersFetchedRef.current = true;
          }
        }
      } catch (error) {
        console.error('Initial load error:', error);
        setStudentsData([]);
      } finally {
        setIsLoading(false);
        initialLoadExecutedRef.current[currentLocationKey] = true; // 실행 완료 표시
      }
    })();
  }, [location.search]); // location.pathname 제거 - search 파라미터만 추적
  
  // 최적화된 더 많은 데이터 로드
  const loadMore = useCallback(() => {
    // 특정 학생/테스트 페이지에서는 loadMore 비활성화
    const isSpecificPage = location.pathname.match(/\/learning-status\/student\/\d+/);
    if (isSpecificPage) {
      console.log('특정 학생/테스트 페이지에서 loadMore 호출 차단:', location.pathname);
      return;
    }
    
    if (!isLoadingMore && hasMore) {
      const nextPage = currentPage + 1;
      
      // 직접 API 호출 (의존성 최적화)
      (async () => {
        try {
          setIsLoadingMore(true);
          const token = await getAccessToken();
          if (!token) {
            navigate('/signin');
            return;
          }

          let url;
          const params = new URLSearchParams({
            page: nextPage,
            per_page: ITEMS_PER_PAGE
          });

          // 필터 적용 (isFilterMode일 때만)
          if (isFilterMode) {
            if (filters.student_name) params.append('student_name', filters.student_name);
            if (filters.in_charge_teacher) params.append('in_charge_teacher', filters.in_charge_teacher);
            if (filters.lecture) params.append('lecture', filters.lecture);
            if (filters.school_name) params.append('school_name', filters.school_name);
          }

          // 권한에 따라 다른 엔드포인트 사용
          if (userType === 'manager') {
            url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
          } else if (userType === 'teacher') {
            params.append('teacher_id', userId);
            url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student/allocate?${params}`;
          } else {
            url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
          }

          const response = await fetch(url, {
            headers: {
              'Authorization': token,
              'Content-Type': 'application/json',
            }
          });

          if (response.ok) {
            const result = await response.json();
            const newStudents = result.data || [];
            
            // 캐시에 저장
            newStudents.forEach(student => {
              studentCacheRef.current.set(student.student_id, student);
            });
            
            setStudentsData(prev => [...prev, ...newStudents]);
            setTotalPages(result.total_pages || 1);
            setHasMore(nextPage < (result.total_pages || 1));
            setCurrentPage(nextPage);
            setTotalItems(result.total_items || 0);
          }
        } catch (error) {
          console.error('LoadMore error:', error);
        } finally {
          setIsLoadingMore(false);
        }
      })();
    }
  }, [currentPage, isLoadingMore, hasMore, isFilterMode, filters, location.pathname]); // location.pathname 추가 (무한 호출 방지)

  // 필터 핸들러 - 메모이제이션으로 리렌더링 최적화
  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  }, []);

    const handleSearch = useCallback(() => {
    setIsFilterMode(true);
    setCurrentPage(1);
    setStudentsData([]);
    
    // fetchStudentsData를 직접 호출하지 않고 상태 변경으로 유도
    const searchFilters = { ...filters };
    
    // 직접 API 호출 (의존성 최적화)
    (async () => {
      try {
        setIsLoading(true);
        const token = await getAccessToken();
        if (!token) {
          navigate('/signin');
          return;
        }

        let url;
        const params = new URLSearchParams({
          page: 1,
          per_page: ITEMS_PER_PAGE
        });

        // 필터 적용
        if (searchFilters.student_name) params.append('student_name', searchFilters.student_name);
        if (searchFilters.in_charge_teacher) params.append('in_charge_teacher', searchFilters.in_charge_teacher);
        if (searchFilters.lecture) params.append('lecture', searchFilters.lecture);
        if (searchFilters.school_name) params.append('school_name', searchFilters.school_name);

        // 권한에 따라 다른 엔드포인트 사용
        if (userType === 'manager') {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
        } else if (userType === 'teacher') {
          params.append('teacher_id', userId);
          url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student/allocate?${params}`;
        } else {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
        }

        const response = await fetch(url, {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        });

                  if (response.ok) {
          const result = await response.json();
          const students = result.data || [];
          
          // 학생 데이터를 캐시에 저장
          students.forEach(student => {
            studentCacheRef.current.set(student.student_id, student);
          });
          
          setStudentsData(students);
          setTotalPages(result.total_pages || 1);
          setHasMore(1 < (result.total_pages || 1));
          setCurrentPage(1);
          setTotalItems(result.total_items || 0);
        }
      } catch (error) {
        console.error('Search error:', error);
        setStudentsData([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [filters]); // 함수 의존성 제거

  const handleReset = useCallback(() => {
    const emptyFilters = {
      student_name: '',
      in_charge_teacher: '',
      lecture: '',
      school_name: ''
    };
    setIsFilterMode(false);
    setFilters(emptyFilters);
    setCurrentPage(1);
    setStudentsData([]);
    
    // 직접 API 호출 (의존성 문제 해결)
    (async () => {
      try {
        setIsLoading(true);
        const token = await getAccessToken();
        if (!token) {
          navigate('/signin');
          return;
        }

        let url;
        const params = new URLSearchParams({
          page: 1,
          per_page: ITEMS_PER_PAGE
        });

        // 권한에 따라 다른 엔드포인트 사용
        if (userType === 'manager') {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
        } else if (userType === 'teacher') {
          params.append('teacher_id', userId);
          url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student/allocate?${params}`;
        } else {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
        }

        const response = await fetch(url, {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const result = await response.json();
          const students = result.data || [];
          
          // 학생 데이터를 캐시에 저장  
          students.forEach(student => {
            studentCacheRef.current.set(student.student_id, student);
          });
          
          setStudentsData(students);
          setTotalPages(result.total_pages || 1);
          setHasMore(1 < (result.total_pages || 1));
          setCurrentPage(1);
        }
      } catch (error) {
        console.error('Reset error:', error);
        setStudentsData([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []); // 함수 의존성 제거

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);
  
  // 학생 상세 정보 모달 - 메모이제이션으로 리렌더링 최적화
  const handleStudentClick = useCallback((student) => {
    setSelectedStudentDetail(student);
    setShowDetailModal(true);
  }, []);
  
  return {
    // 데이터
    studentsData,
    isLoading: isLoading || isLoadingSingleStudent, // 단일 학생 로딩도 포함
    isLoadingMore,
    error: error || singleStudentError, // 두 에러를 합침
    
    // Teacher 데이터 (통합 관리)
    teachers,
    isTeachersLoading,
    fetchTeachers,
    
    // 단일 학생 조회 상태 (추가)
    isLoadingSingleStudent,
    singleStudentError,
    
    // 필터
    filters,
    isFilterMode,
    handleFilterChange,
    handleKeyPress,
    handleSearch,
    handleReset,
    
    // 인피니티 스크롤
    currentPage,
    totalPages,
    hasMore,
    loadMore,
    totalItems,
    
    // 모달
    showDetailModal,
    setShowDetailModal,
    selectedStudentDetail,
    setSelectedStudentDetail,
    handleStudentClick,

    // 단일 학생 조회
    fetchStudentById,
    
    // 전체 목록 복원
    resetToFullList: useCallback(() => {
      console.log('수동 전체 목록 복원 트리거');
      setShouldResetToFullList(true);
    }, [])
  };
}; 